// ui/Shell.tsx
// （用這份全文直接覆蓋整個檔案；本版重點：修正 Dashboard 三卡 header 排版：名字永遠不被擠掉、不斷行、不消失；細項不再出現兩個「其他」，改為三排 chips）

import React, { useState, useEffect } from "react";
import type { Session } from "./auth";
import { Sidebar, type NavKey } from "./Sidebar";

// ... imports of pages (兼容：支援 named export / default export 任一種寫法)
// 目的：避免因 pages export 形式變動，導致橘雲/Vite build 在 Shell 匯入階段直接失敗。
type AnyPage = React.ComponentType<any>;
function pickPage(mod: any, named: string): AnyPage {
  const Comp = mod?.[named] ?? mod?.default;
  if (Comp) return Comp as AnyPage;

  // Fallback：避免渲染時整頁白畫面（同時保留可見錯誤訊息）
  return function MissingPage() {
    return (
      <div style={{ padding: 16, color: "var(--danger)" }}>
        <h2 style={{ margin: "0 0 8px 0" }}>Page Export Missing</h2>
        <p style={{ margin: 0 }}>
          頁面模組未提供 <code>{named}</code> 或 <code>default</code> export。
        </p>
      </div>
    );
  };
}

import * as LegalMattersPageMod from "./pages/LegalMattersPage";
import * as IpMattersPageMod from "./pages/IpMattersPage";
import * as BrandAssetsPageMod from "./pages/BrandAssetsPage";
import * as LawyerCoopPageMod from "./pages/LawyerCoopPage";
import * as ContractsPageMod from "./pages/ContractsPage";
import * as ContractReviewsPageMod from "./pages/ContractReviewsPage";
import * as CalendarPageMod from "./pages/CalendarPage";
import * as TemplatesPageMod from "./pages/TemplatesPage";
import * as GovernanceCoreDocsPageMod from "./pages/GovernanceCoreDocsPage";
import * as TasksAndLogsPageMod from "./pages/TasksAndLogsPage";
import * as GovCorrespondencePageMod from "./pages/GovCorrespondencePage";
import * as SealApplicationPageMod from "./pages/SealApplicationPage";
import * as AuditLogPageMod from "./pages/AuditLogPage";
import * as StorageDebugPageMod from "./pages/StorageDebugPage";
import * as NotificationHistoryPageMod from "./pages/NotificationHistoryPage";
import * as ComplianceControlCenterPageMod from "./pages/ComplianceControlCenterPage";
import * as LegacyContractsPageMod from "./pages/LegacyContractsPage";

const LegalMattersPage = pickPage(LegalMattersPageMod, "LegalMattersPage");
const IpMattersPage = pickPage(IpMattersPageMod, "IpMattersPage");
const BrandAssetsPage = pickPage(BrandAssetsPageMod, "BrandAssetsPage");
const LawyerCoopPage = pickPage(LawyerCoopPageMod, "LawyerCoopPage");
const ContractsPage = pickPage(ContractsPageMod, "ContractsPage");
const ContractReviewsPage = pickPage(ContractReviewsPageMod, "ContractReviewsPage");
const CalendarPage = pickPage(CalendarPageMod, "CalendarPage");
const TemplatesPage = pickPage(TemplatesPageMod, "TemplatesPage");
const GovernanceCoreDocsPage = pickPage(GovernanceCoreDocsPageMod, "GovernanceCoreDocsPage");
const TasksAndLogsPage = pickPage(TasksAndLogsPageMod, "TasksAndLogsPage");
const GovCorrespondencePage = pickPage(GovCorrespondencePageMod, "GovCorrespondencePage");
const SealApplicationPage = pickPage(SealApplicationPageMod, "SealApplicationPage");
const AuditLogPage = pickPage(AuditLogPageMod, "AuditLogPage");
const SystemDebugPage = pickPage(StorageDebugPageMod, "StorageDebugPage");
const NotificationHistoryPage = pickPage(NotificationHistoryPageMod, "NotificationHistoryPage");
const ComplianceControlCenterPage = pickPage(ComplianceControlCenterPageMod, "ComplianceControlCenterPage");
const LegacyContractsPage = pickPage(LegacyContractsPageMod, "LegacyContractsPage");

import { GlobalSearchBar } from "./components/GlobalSearchBar";
import { AIAssistantDrawer } from "./ai/AIAssistantDrawer";
import { AiFloatingButton } from "./ai/AiFloatingButton";
import { GlobalProvider, useAiContext } from "./ai/AiContext";
import { ToastProvider, useToast } from "./components/Toast";
import { ConfirmDeleteModal } from "./components/ConfirmDeleteModal";
import { useAuth } from "./context/AuthContext";
import { fetchDashboardStats } from "./services/dashboard";
import type { DashboardStats } from "./services/dashboard";
import { getCalendarEventsBetween } from "./services/calendarService";
import { markAllAsRead } from "./services/notificationService";
import { db } from "../firebaseClient";
import type { NotificationDoc, CalendarEventDoc } from "./types/legalOps";
import { canViewWorklog, canViewAuditLog } from "./config/roles";
import { formatDate, formatDateMD, compareDateLike } from "./utils/dateUtils";
import type { DailySummaryResult, UserRealtimeSummary, PendingItem, PendingModule } from "./services/dailySummaryService";
import { subscribeTrueTodoKpiSummary, getTrueTodoKpiSummaryOnce, triggerRebuildTrueTodoKpiSummaryNow } from "./services/trueTodoKpiSummaryService";
import type { TrueTodoKpiSummary } from "./services/trueTodoKpiSummaryService";
import { seedDemoDataBNow } from "./services/demoSeedBService";
import { useTodosLiveMonitor } from "./hooks/useTodosLiveMonitor";
import { getWarmQuoteForSession } from "./config/warmQuotes";

// Helper: Pick a warm quote (rotates daily / per session, and avoids duplicates across three users)
function pickWarmQuote(userKey: "kevin" | "ops" | "gm"): string {
  return getWarmQuoteForSession(userKey);
}

const TITLES: Record<string, { title: string; desc: string }> = {
  dashboard: { title: "法務總覽儀錶板", desc: "關鍵指標、待辦提醒與近期更新。" },
  contracts: { title: "合約系統管理", desc: "合約生命週期、審閱與簽署進度。" },
  contractReviews: { title: "合約草案審查", desc: "合約審閱案件、優先順序與時效控管。" },
  legalMatters: { title: "案件及爭議管理", desc: "訴訟、非訟、消費爭議與其他紛爭處理。" },
  ipMatters: { title: "智財申請與維權", desc: "商標、專利、著作權申請與侵權維權。" },
  brandAssets: { title: "品牌資產管理", desc: "商標、IP 授權與品牌佈局導圖。" },
  lawyerCoop: { title: "顧問律師管理", desc: "外部律師事務所與顧問合作清單。" },
  sealApplication: { title: "用印申請與紀錄", desc: "公司大小章、合約用印流程追蹤。" },
  govCorrespondence: { title: "公文來文與函覆管理", desc: "主管機關來函、裁處與公文回覆追蹤。" },
  templates: { title: "範本中心", desc: "標準合約與法律文件範本庫。" },
  governanceCoreDocs: { title: "公司治理核心文件庫", desc: "董事會、內控制度與風險管理辦法。" },
  tasksAndLogs: { title: "待辦事項與法務工作日誌", desc: "法務團隊工作清單與處理歷程。" },
  audit: { title: "稽核、簽核及操作紀錄", desc: "系統操作歷程、簽核歷程與稽核軌跡。" },
  calendar: { title: "行事曆", desc: "案件、爭議、合約到期與授權期限月曆。" },
  notificationHistory: { title: "通知歷史紀錄", desc: "系統發送之所有通知與提醒。" },
  storageDebug: { title: "系統檢測 (Debug)", desc: "檔案上傳測試與報表預覽" },
  complianceControlCenter: { title: "法遵情資與專案管控中心", desc: "法規變動監測、專案管控與風險評估。" },
  legacyContracts: {
    title: "舊合約登錄系統 (舊合約建檔專用)",
    desc: "僅用於輸入既有紙本／掃描舊合約的基本資料，送出後將併入主合約資料庫。"
  }
};

function DashboardUserCard({ user, headerColor }: { user: UserRealtimeSummary; headerColor: string }) {
  const c = user.counts;
  const pending = user.pendingList;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isLegalUser = user.userKey === "kevin";
  const [domainCounts, setDomainCounts] = useState<{ casesOpen: number; disputesOpen: number; brandPending: number; legacyDrafts: number }>(() => ({
    casesOpen: 0,
    disputesOpen: 0,
    brandPending: 0,
    legacyDrafts: 0,
  }));

  useEffect(() => {
    if (!isLegalUser) return;

    const normalize = (v: any) => String(v ?? "").trim();
    const isOpen = (s: string) => s.startsWith("開案中") || s.startsWith("進行中");
    const isDraftOrInReview = (s: string) => {
      const x = s.trim();
      const lower = x.toLowerCase();
      return (
        x.includes("草稿") ||
        x.includes("送審") ||
        x.includes("審核") ||
        x.includes("簽核") ||
        lower === "draft" ||
        lower === "in_review" ||
        lower === "in-review"
      );
    };

    // 1) 案件 + 爭議：只算「開案中/進行中」(排除已結案/已轉正式)
    const unsubCases = db.collection("legal_cases").onSnapshot(
      (snap) => {
        let count = 0;
        snap.forEach((d) => {
          const s = normalize((d.data() as any)?.progressStatus);
          if (isOpen(s)) count += 1;
        });
        setDomainCounts((prev) => ({ ...prev, casesOpen: count }));
      },
      () => {
        // ignore; keep last value
      }
    );

    const unsubDisputes = db.collection("legal_disputes").onSnapshot(
      (snap) => {
        let count = 0;
        snap.forEach((d) => {
          const s = normalize((d.data() as any)?.status);
          if (s === "已轉為正式案件") return;
          if (isOpen(s)) count += 1;
        });
        setDomainCounts((prev) => ({ ...prev, disputesOpen: count }));
      },
      () => {
        // ignore; keep last value
      }
    );

    // 2) 品牌資產申請：草稿 + 送審/審核/簽核中都算（保守相容，避免漏）
    // 目前資料主要在 brand_assets，若未來加入 workflow 欄位也會自動涵蓋。
    const unsubBrand = db.collection("brand_assets").onSnapshot(
      (snap) => {
        let count = 0;
        snap.forEach((d) => {
          const data: any = d.data();
          const s = normalize(data?.status || data?.workflowStatus || data?.approvalStatus);
          if (isDraftOrInReview(s)) count += 1;
        });
        setDomainCounts((prev) => ({ ...prev, brandPending: count }));
      },
      () => {
        // ignore; keep last value
      }
    );

    // 3) 舊合約登錄草稿：legacyContracts 集合內尚未「確認存檔」的都算（草稿/送審中）
    const unsubLegacy = db.collection("legacyContracts").onSnapshot(
      (snap) => {
        let count = 0;
        snap.forEach((d) => {
          const data: any = d.data();
          const s = normalize(data?.status || data?.workflowStatus || data?.approvalStatus);
          // legacyContracts 的 status 目前是 draft；但用同一判斷，避免未來擴充時漏算。
          if (isDraftOrInReview(s) || !s) count += 1;
        });
        setDomainCounts((prev) => ({ ...prev, legacyDrafts: count }));
      },
      () => {
        // ignore; keep last value
      }
    );

    return () => {
      unsubCases();
      unsubDisputes();
      unsubBrand();
      unsubLegacy();
    };
  }, [isLegalUser]);

  const chip: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    fontSize: 12,
    whiteSpace: "nowrap",
  };

  const headerWrap: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  };

  const nameStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "70%",
    color: "#e5e7eb",
  };

  const totalChip: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
    flexShrink: 0,
  };

  const categories = [
    { label: "合約會審", value: c.contractReviews },
    { label: "範本", value: c.templates },
    { label: "用印", value: c.seals },
    { label: "案件與爭議", value: c.cases },
    { label: "公文函覆", value: c.govCorrespondence },
    { label: "法遵情資", value: c.regIntel },
    { label: "專案管控", value: c.projectControl },
    { label: "核心文件工作區", value: c.governanceWorkspace },
  ];

  // ✅ 法務卡：額外三個 domain pills（總案量）
  const extraCategories = isLegalUser
    ? [
        { label: "案件及爭議管理", value: domainCounts.casesOpen + domainCounts.disputesOpen },
        { label: "品牌資產申請", value: domainCounts.brandPending },
        { label: "舊合約登錄草稿", value: domainCounts.legacyDrafts },
      ]
    : [];

  // ✅ 法務卡片的總待辦要把 extraCategories 一起算進去
  const extraTotal = isLegalUser
    ? domainCounts.casesOpen + domainCounts.disputesOpen + domainCounts.brandPending + domainCounts.legacyDrafts
    : 0;
  const displayTotal = (c.total || 0) + extraTotal;

  const allCategories = [...categories, ...extraCategories];

  // 依 3 個一排自動換行（避免擠爆）
  const rows: Array<Array<{ label: string; value: number }>> = [];
  for (let i = 0; i < allCategories.length; i += 3) {
    rows.push(allCategories.slice(i, i + 3));
  }

  // Flatten pending list（把 8 分類都攤平成一張清單）
  const allItems: PendingItem[] = [];
  (Object.values(pending || {}) as any[]).forEach((arr) => {
    if (Array.isArray(arr)) allItems.push(...arr);
  });

  const toMs = (x: any): number | null => {
    if (!x) return null;
    if (typeof x === "number" && isFinite(x)) return x;
    if (typeof x === "string") {
      const t = Date.parse(x);
      return isNaN(t) ? null : t;
    }
    if (x instanceof Date) return x.getTime();

    // Firestore Timestamp-like
    if (typeof x === "object") {
      const sec = (x as any).seconds;
      const nanos = (x as any).nanoseconds;
      if (typeof sec === "number") {
        const ms = sec * 1000 + (typeof nanos === "number" ? Math.floor(nanos / 1e6) : 0);
        return isFinite(ms) ? ms : null;
      }
      if (typeof (x as any).toMillis === "function") {
        try {
          const ms = (x as any).toMillis();
          return typeof ms === "number" && isFinite(ms) ? ms : null;
        } catch {
          return null;
        }
      }
    }

    return null;
  };

  const normStage = (v: any) => String(v || "").trim().toUpperCase().replace(/\s+/g, "_").replace(/-/g, "_");

  const pickFirstMs = (...cands: any[]): number | null => {
    for (const c of cands) {
      const ms = toMs(c);
      if (typeof ms === "number" && isFinite(ms)) return ms;
    }
    return null;
  };

  // 取得「在此關卡」的起算時間：
  // - LEGAL_REVIEW：submittedAt/createdAt
  // - MANAGER_OPS：LEGAL_REVIEW.actedAt
  // - MANAGER_GM：MANAGER_OPS.actedAt
  // - 其他：updatedAt/createdAt
  const getStageStartMs = (p: PendingItem): number | null => {
    const raw: any = (p as any)?.raw;
    const stageKey = normStage((p as any)?.stageKey || raw?.currentStageKey || raw?.stageKey || "");
    const stages = Array.isArray(raw?.stages) ? raw.stages : [];

    const findStage = (k: string) => {
      const kk = normStage(k);
      return stages.find((s: any) => normStage(s?.key || s?.stageKey || s?.id) === kk) || null;
    };

    if (stageKey === "MANAGER_OPS") {
      const s = findStage("LEGAL_REVIEW");
      return pickFirstMs(s?.actedAt, raw?.updatedAt, p.updatedAt, raw?.createdAt, p.createdAt);
    }

    if (stageKey === "MANAGER_GM") {
      const s = findStage("MANAGER_OPS");
      const s2 = findStage("LEGAL_REVIEW");
      return pickFirstMs(s?.actedAt, s2?.actedAt, raw?.updatedAt, p.updatedAt, raw?.createdAt, p.createdAt);
    }

    if (stageKey === "LEGAL_REVIEW") {
      return pickFirstMs(raw?.submittedAt, raw?.createdAt, p.createdAt, raw?.updatedAt, p.updatedAt);
    }

    return pickFirstMs(raw?.updatedAt, p.updatedAt, raw?.createdAt, p.createdAt);
  };

  const calcStagnantDays = (p: PendingItem): number => {
    const startMs = getStageStartMs(p);
    if (!startMs) return 0;
    const days = Math.floor((Date.now() - startMs) / 86400000);
    return days < 0 ? 0 : days;
  };

  const stagnantTone = (days: number): React.CSSProperties => {
    if (days >= 7) return { color: "var(--danger)", fontWeight: 900 };
    if (days >= 4) return { color: "#fbbf24", fontWeight: 900 };
    return { color: "var(--ok)", fontWeight: 900 };
  };

  const sorted = [...allItems].sort((a, b) => {
    const da = calcStagnantDays(a);
    const db = calcStagnantDays(b);
    if (db !== da) return db - da;
    const aa = `${a.module}-${a.docNo || a.id}`;
    const bb = `${b.module}-${b.docNo || b.id}`;
    return aa.localeCompare(bb);
  });

  const toggleDrawer = () => setDrawerOpen((v) => !v);
  const onCardKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleDrawer();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={toggleDrawer}
      onKeyDown={onCardKeyDown}
      style={{
        padding: 12,
        borderRadius: 14,
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${headerColor}`,
        cursor: "pointer",
        outline: "none",
      }}
    >
      <div style={headerWrap}>
        <div style={nameStyle}>{user.displayName}</div>
          <div style={totalChip}>總待辦 {displayTotal}</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, idx) => (
          <div key={idx} style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {r.map((x) => (
              <span key={x.label} style={chip}>
                <span style={{ opacity: 0.85 }}>{x.label}</span>
                <span style={{ fontWeight: 900 }}>{x.value}</span>
              </span>
            ))}
          </div>
        ))}
      </div>

      <div
        onClick={(e) => {
          // ✅ 展開後在清單內點擊/捲動，不要觸發整張卡片的收合
          if (drawerOpen) e.stopPropagation();
        }}
        style={{
          marginTop: 10,
          padding: 10,
          borderRadius: 12,
          background: "rgba(0,0,0,0.22)",
          border: "1px solid rgba(255,255,255,0.10)",
          cursor: "default",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 900 }}>待辦清單（{sorted.length}）</span>
          <span style={{ opacity: 0.7, fontSize: 12 }}>
            {drawerOpen ? "點卡片收合" : "點卡片展開"} <span style={{ opacity: 0.85 }}>{drawerOpen ? "▾" : "▸"}</span>
          </span>
        </div>

        {!drawerOpen ? null : sorted.length === 0 ? (
          <div style={{ paddingTop: 10, opacity: 0.75 }}>目前沒有待辦</div>
        ) : (
          <div style={{ marginTop: 10, maxHeight: 360, overflow: "auto", paddingRight: 4 }}>
            {sorted.map((p) => {
              const days = calcStagnantDays(p);
              const key = `${p.module}-${p.docNo || p.id}-${p.source}-${p.rawId || ""}`;
              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "6px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    <span
                      style={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        opacity: 0.92,
                      }}
                    >
                      {String(p.docNo || p.id || "(無編號)")}
                    </span>
                    <span style={{ margin: "0 8px", opacity: 0.35 }}>·</span>
                    <span style={{ opacity: 0.92 }}>{labelFromModule(p.module)}</span>
                    <span style={{ margin: "0 8px", opacity: 0.35 }}>·</span>
                    <span style={{ opacity: 0.96 }}>{p.title || "(未命名)"}</span>
                  </div>
                  <div style={{ flexShrink: 0, ...stagnantTone(days) }}>{days}天</div>
                </div>
              );
            })}
          </div>
        )}

        {!drawerOpen ? null : (
          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
            停滯時間：<span style={{ color: "var(--ok)", fontWeight: 900 }}>0–3天</span> /{" "}
            <span style={{ color: "#fbbf24", fontWeight: 900 }}>4–6天</span> /{" "}
            <span style={{ color: "var(--danger)", fontWeight: 900 }}>7天以上</span>
          </div>
        )}
      </div>

      {(() => {
        const quote = pickWarmQuote(user.userKey);
        if (!quote) return null;
        return (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              lineHeight: 1.5,
              padding: "8px 10px",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              background: "rgba(255,255,255,0.04)",
              opacity: 0.95,
            }}
          >
            <span
              style={{
                fontWeight: 900,
                backgroundImage: "linear-gradient(90deg, #fbbf24, #60a5fa, #34d399)",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              微光小語：
            </span>
            <span style={{ marginLeft: 6, opacity: 0.92 }}>{quote}</span>
          </div>
        );
      })()}
    </div>
  );
}

function labelFromModule(m: PendingModule): string {
  switch (m) {
    case "contractReviews":
      return "合約會審";
    case "templates":
      return "範本";
    case "seals":
      return "用印";
    case "cases":
      return "案件與爭議";
    case "govCorrespondence":
      return "公文函覆";
    case "regIntel":
      return "法遵情資";
    case "projectControl":
      return "專案管控";
    case "governanceWorkspace":
      return "核心文件工作區";
    default:
      return "待辦";
  }
}

function LegalDashboardDailySummaryCard() {
  // ✅ Live Monitor（前端）：Approvals + Raw Todos
  const monitor = useTodosLiveMonitor(true);
  const { showToast } = useToast();
  const [isSeedingDemo, setIsSeedingDemo] = useState(false);


  const data = (monitor.data as DailySummaryResult | null)
    ? {
        ...(monitor.data as DailySummaryResult),
        users: (monitor.data as DailySummaryResult).users.map((u) => {
          if (u.userKey === "ops") return { ...u, displayName: "財務長" };
          if (u.userKey === "gm") return { ...u, displayName: "總經理" };
          return u;
        }),
      }
    : null;

  const liveStatus = monitor.liveStatus || "connecting";
  const liveMsg = monitor.liveMsg || "初始化...";

  const reconnect = async () => {
    try {
      await (monitor.reconnect?.() as any);
    } catch {
      // ignore
    }
  };

  return (
    <div style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#e5e7eb" }}>
          C. 真待辦三卡（Approvals + Raw Todos）✅ KPI
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={reconnect}
            style={{ fontSize: 12, padding: "6px 10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#e5e7eb", cursor: "pointer" }}
            title="重新同步（純前端監控，不依賴 functions）"
          >
            🔄 重新同步
          </button>

          <button
            type="button"
            onClick={async () => {
              try {
                setIsSeedingDemo(true);
                const res = await seedDemoDataBNow();
                if (res?.ok) {
                  const total = Object.values(res.counts || {}).reduce((a, b) => a + (Number(b) || 0), 0);
                  showToast(`已建立 Demo(B)：共 ${total} 筆｜${res.message || "完成"}`, "success");
                  reconnect(); // refresh monitor summary
                } else {
                  showToast(`建立 Demo(B) 失敗：${res?.message || "unknown error"}`, "error");
                }
              } catch (e: any) {
                let msg = "unknown error";
                if (typeof e === "string") {
                  msg = e;
                } else {
                  try {
                    msg = JSON.stringify(e);
                  } catch {
                    msg = String(e);
                  }
                }
                showToast(`建立 Demo(B) 失敗：${msg}`, "error");
              } finally {
                setIsSeedingDemo(false);
              }
            }}
            disabled={isSeedingDemo}
            style={{
              fontSize: 12,
              padding: "6px 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: isSeedingDemo ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.06)",
              color: isSeedingDemo ? "#9ca3af" : "#e5e7eb",
              cursor: isSeedingDemo ? "not-allowed" : "pointer",
            }}
            title="建立 demo 資料（B版）。會新增多筆案例，並各自產生獨立附件。"
          >
            🌱 {isSeedingDemo ? "建立中..." : "建立 Demo(B)"}
          </button>

          <span style={{ fontSize: 12, color: "#9ca3af" }}>
            {monitor.networkOnline ? "ONLINE" : "OFFLINE"}
          </span>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: liveStatus === "error" ? "#fca5a5" : "#9ca3af" }}>
        {liveMsg}
      </div>

      <div style={{ marginTop: 14 }}>
        {!data ? (
          <div style={{ color: "#9ca3af", fontSize: 12 }}>載入中...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            {data.users.map((u, i) => (
              <DashboardUserCard
                key={u.userKey}
                user={u}
                headerColor={i === 0 ? "#93c5fd" : i === 1 ? "#a7f3d0" : "#fde68a"}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function ShellInner({ session, onLogout, bootErr }: { session: Session; onLogout: () => void; bootErr?: any }) {

  // 顯示名稱修正：ops → 財務長、gm → 總經理（只影響畫面文字，不改角色/權限）
  const mappedSessionDisplayName = (() => {
    const raw = (session?.displayName || "").toString().trim();
    const key = raw.toLowerCase();
    if (key === "ops") return "財務長";
    if (key === "gm") return "總經理";
    return raw;
  })();

  const { isSealUser, user } = useAuth();
  const { showToast } = useToast();

  const [active, setActive] = React.useState<NavKey>(() => {
    if (session.role === "legacyDataEntry") return "legacyContracts";
    if (session.role === "sealUser") return "contractReviews";
    if (session.role === "dept-minimal") return "sealApplication";
    return "dashboard";
  });

  const { setActiveItem, isAiOpen, setAiOpen, deleteDialogState, closeDeleteDialog } = useAiContext();
  const [notifs, setNotifs] = React.useState<NotificationDoc[]>([]);
  const [dashStats, setDashStats] = React.useState<DashboardStats | null>(null);
  const [recentEvents, setRecentEvents] = React.useState<CalendarEventDoc[]>([]);
  const [showNotifPanel, setShowNotifPanel] = React.useState(false);

  useEffect(() => {
    if (!isSealUser && session.role !== "dept-minimal" && session.role !== "legacyDataEntry") {
      const q = db.collection("notifications").where("read", "==", false).orderBy("createdAt", "desc").limit(20);
      const unsubscribe = q.onSnapshot((snapshot: any) => {
        const dbNotifs = snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as NotificationDoc));
        setNotifs(dbNotifs);
      });
      return () => unsubscribe();
    } else {
      setNotifs([]);
    }
  }, [isSealUser, session.role]);

  useEffect(() => {
    if (active === "dashboard" && !isSealUser && session.role !== "dept-minimal" && session.role !== "legacyDataEntry") {
      fetchDashboardStats().then(setDashStats).catch(console.error);
      const today = new Date();
      const next30 = new Date();
      next30.setDate(today.getDate() + 30);
      getCalendarEventsBetween(formatDate(today), formatDate(next30))
        .then((events) => {
          events.sort((a: any, b: any) => compareDateLike(a.date, b.date));
          setRecentEvents(events.slice(0, 5));
        })
        .catch(console.error);
    }
  }, [active, isSealUser, session.role]);

  // 保持原本的 NavKey handler（Sidebar / GlobalSearchBar 用）
  const handleNavigate = (key: NavKey, id?: string) => {
    if (session.role === "legacyDataEntry") return;
    setActive(key);
  };

  const handleNotifClick = (n: NotificationDoc) => {
    const mapping: Record<string, NavKey> = {
      contract: "contracts",
      case: "legalMatters",
      dispute: "legalMatters",
      brandAsset: "brandAssets",
      task: "tasksAndLogs",
      govCorrespondence: "govCorrespondence",
      seal: "sealApplication",
      governance: "governanceCoreDocs"
    };
    if (n.sourceType && mapping[n.sourceType]) {
      setActive(mapping[n.sourceType] as NavKey);
    }
    setShowNotifPanel(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifs([]);
      showToast("已全部標示為已讀");
    } catch (e) {
      console.error(e);
      showToast("操作失敗", "error");
    }
  };

  const renderContent = () => {
    const email = user?.email || "";
    const isSpecialAdmin = ["kc@lg.app", "ad@lg.app"].includes(email.toLowerCase());

    if (active === "legacyContracts") {
      if (session.role === "legacyDataEntry" || isSpecialAdmin) {
        return <LegacyContractsPage />;
      }
      return <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>權限不足 (403)</div>;
    }
    if (session.role === "legacyDataEntry") return <LegacyContractsPage />;

    if (session.role === "dept-minimal") {
      const allowed = ["governanceCoreDocs", "sealApplication", "contractReviews", "complianceControlCenter"];
      if (!allowed.includes(active)) {
        return <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>權限不足 (403): 此帳號僅限存取特定功能</div>;
      }
    }

    if (active === "tasksAndLogs" && !canViewWorklog(email)) return <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>權限不足 (403): 僅限法務/管理員存取</div>;
    if (active === "audit" && !canViewAuditLog(email)) return <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>權限不足 (403): 僅限法務/管理員存取</div>;
    if (active === "storageDebug" && !isSpecialAdmin) return <div className="p" style={{ padding: 40, textAlign: "center", color: "var(--danger)" }}>權限不足</div>;

    switch (active) {
      case "dashboard":
        return !dashStats ? (
          <div className="p" style={{ padding: 20, textAlign: "center" }}>載入儀錶板數據...</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <LegalDashboardDailySummaryCard />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
              <div className="tile">
                <div style={{ borderBottom: "1px solid #333", paddingBottom: 8, marginBottom: 10, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                  <span>合約總覽</span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>Active: {dashStats.contracts.active}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-around", textAlign: "center" }}>
                  <div><div className="h1" style={{ color: "var(--ok)" }}>{dashStats.contracts.active}</div><div className="p">有效合約</div></div>
                  <div><div className="h1" style={{ color: "#fbbf24" }}>{dashStats.contracts.expiringIn90Days}</div><div className="p">90天內到期</div></div>
                  <div><div className="h1" style={{ color: "var(--danger)" }}>{dashStats.contracts.highRiskAmount}</div><div className="p">高額合約</div></div>
                </div>
              </div>

              <div className="tile">
                <div style={{ borderBottom: "1px solid #333", paddingBottom: 8, marginBottom: 10, fontWeight: "bold" }}>案件與法遵</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ textAlign: "center", background: "#222", padding: 10, borderRadius: 8 }}>
                    <div className="h1">{dashStats.cases.ongoing}</div>
                    <div className="p">進行中案件</div>
                  </div>
                  <div style={{ textAlign: "center", background: "#222", padding: 10, borderRadius: 8 }}>
                    <div className="h1" style={{ color: "#fbbf24" }}>{dashStats.compliance.thisMonthCount}</div>
                    <div className="p">本月應行專案</div>
                  </div>
                </div>
              </div>

              <div className="tile">
                <div style={{ borderBottom: "1px solid #333", paddingBottom: 8, marginBottom: 10, fontWeight: "bold" }}>待辦與簽核</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>待審合約草案</span><b style={{ color: "#8b5cf6" }}>{dashStats.reviews.pendingContracts}</b></div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>待核用印</span><b style={{ color: "#8b5cf6" }}>{dashStats.reviews.pendingSealRequests}</b></div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>待辦公文</span><b style={{ color: "#8b5cf6" }}>{dashStats.reviews.pendingGovLetters}</b></div>
                  <div style={{ borderTop: "1px dashed #444", marginTop: 4, paddingTop: 4, display: "flex", justifyContent: "space-between" }}><span>未讀通知</span><b style={{ color: "var(--danger)" }}>{dashStats.notifications.unreadCount}</b></div>
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 20 }}>
              <div className="section" style={{ minHeight: 250 }}>
                <div style={{ borderBottom: "1px solid #333", paddingBottom: 10, marginBottom: 12, fontWeight: "bold", fontSize: 16, display: "flex", justifyContent: "space-between" }}>
                  <span>近期重要提醒 (30天內)</span>
                  <button className="smallbtn" style={{ fontSize: 11 }} onClick={() => setActive("calendar")}>前往行事曆</button>
                </div>
                {recentEvents.length === 0 ? (
                  <div className="p" style={{ textAlign: "center", marginTop: 20 }}>近期無重要事項</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {recentEvents.map((e, idx) => (
                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #333" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ textAlign: "center", minWidth: 40 }}>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>{formatDateMD(e.date)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{e.title}</div>
                            <div style={{ fontSize: 12, color: "var(--muted)" }}>{e.note || e.type || "一般事件"}</div>
                          </div>
                        </div>
                        {e.severity === "upcoming" && <span className="badge" style={{ color: "#fbbf24" }}>即將到期</span>}
                        {e.severity === "overdue" && <span className="badge" style={{ color: "var(--danger)" }}>已過期</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="section" style={{ minHeight: 250 }}>
                <div style={{ borderBottom: "1px solid #333", paddingBottom: 10, marginBottom: 12, fontWeight: "bold", fontSize: 16, display: "flex", justifyContent: "space-between" }}>
                  <span>最新通知 (未讀)</span>
                  <button className="smallbtn" style={{ fontSize: 11 }} onClick={() => setActive("notificationHistory")}>查看全部紀錄</button>
                </div>
                {notifs.length === 0 ? (
                  <div className="p" style={{ textAlign: "center", marginTop: 20 }}>目前沒有新通知</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {notifs.slice(0, 5).map((n) => (
                      <div key={n.id} onClick={() => handleNotifClick(n)} style={{ cursor: "pointer", padding: "8px", borderRadius: 6, background: "rgba(255,255,255,0.03)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: "bold", color: "#e5e7eb" }}>{n.title}</span>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>{n.date || n.eventDate}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.detail}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "legalMatters": return <LegalMattersPage />;
      case "ipMatters": return <IpMattersPage />;
      case "brandAssets": return <BrandAssetsPage />;
      case "lawyerCoop": return <LawyerCoopPage role={session.role} />;
      case "sealApplication": return <SealApplicationPage />;
      case "govCorrespondence": return <GovCorrespondencePage />;
      case "contracts": return <ContractsPage />;
      case "contractReviews": return <ContractReviewsPage />;

      // ✅ 修正點：用 wrapper 讓 CalendarPage 看到 (string,string) 版本
      case "calendar":
        return <CalendarPage onNavigate={(key: string, id: string) => handleNavigate(key as NavKey, id)} />;

      case "templates": return <TemplatesPage />;
      case "governanceCoreDocs": return <GovernanceCoreDocsPage />;
      case "tasksAndLogs": return <TasksAndLogsPage />;
      case "audit": return <AuditLogPage />;
      case "notificationHistory": return <NotificationHistoryPage />;
      case "storageDebug": return <SystemDebugPage onNavigate={handleNavigate} />;
      case "complianceControlCenter": return <ComplianceControlCenterPage />;
      default: return <div className="p">開發中</div>;
    }
  };

  return (
    <div className="shell">
      <Sidebar
        active={active}
        onSelect={(k) => {
          setActive(k);
          setActiveItem(null);
        }}
        role={session.role}
        username={mappedSessionDisplayName}
        onLogout={onLogout}
        onOpenSettings={() => { }}
        onOpenShortcuts={() => { }}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          style={{
            height: 64,
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            background: "var(--bg)",
            gap: 20
          }}
        >
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <GlobalSearchBar onNavigate={handleNavigate} />
          </div>

          <div style={{ position: "relative", flexShrink: 0 }}>
            <button
              onClick={() => setShowNotifPanel(!showNotifPanel)}
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "var(--text)",
                fontSize: 18
              }}
            >
              🔔
              {notifs.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    background: "var(--danger)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: "bold",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid var(--bg)"
                  }}
                >
                  {notifs.length > 9 ? "9+" : notifs.length}
                </span>
              )}
            </button>

            {showNotifPanel && (
              <div
                style={{
                  position: "absolute",
                  top: "120%",
                  right: 0,
                  width: 320,
                  maxHeight: 450,
                  overflowY: "auto",
                  background: "#1e1e1e",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
                  zIndex: 3000,
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #333",
                    fontWeight: "bold",
                    fontSize: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <span>通知中心</span>
                  {notifs.length > 0 && (
                    <button className="smallbtn" style={{ fontSize: 10, padding: "2px 6px", height: "auto" }} onClick={handleMarkAllRead}>
                      全部已讀
                    </button>
                  )}
                </div>

                <div style={{ flex: 1, overflowY: "auto" }}>
                  {notifs.length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>目前沒有未讀通知</div>
                  ) : (
                    notifs.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        style={{
                          padding: "12px 16px",
                          borderBottom: "1px solid #333",
                          cursor: "pointer",
                          transition: "background 0.2s"
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#2a2a2a")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span className="badge" style={{ fontSize: 10, padding: "2px 6px" }}>{n.category}</span>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>{n.eventDate}</span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#e5e7eb", marginBottom: 2 }}>{n.title}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.4 }}>{n.detail}</div>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ borderTop: "1px solid #333", padding: 8, textAlign: "center" }}>
                  <button
                    className="smallbtn"
                    style={{ width: "100%", background: "transparent", border: "none", color: "var(--muted)" }}
                    onClick={() => {
                      setActive("notificationHistory");
                      setShowNotifPanel(false);
                    }}
                  >
                    查看歷史通知紀錄
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="main" style={{ flex: 1, overflowY: "auto", padding: "18px" }}>
          <div className="lop-page-header" style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: "bold", color: "#fff" }}>{TITLES[active]?.title}</h2>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>{TITLES[active]?.desc}</div>
          </div>
          {renderContent()}
        </div>
      </div>

      <AIAssistantDrawer isOpen={isAiOpen} onClose={() => setAiOpen(false)} context={active} />
      {!isSealUser && session.role !== "dept-minimal" && <AiFloatingButton />}

      {deleteDialogState && (
        <ConfirmDeleteModal
          isOpen={!!deleteDialogState}
          title="確認刪除"
          message={`您確定要刪除「${deleteDialogState.summary}」嗎？`}
          onConfirm={() => {
            deleteDialogState.onConfirm();
            closeDeleteDialog();
          }}
          onCancel={closeDeleteDialog}
        />
      )}
    </div>
  );
}

export function Shell(props: { session: Session; onLogout: () => void; bootErr?: any }) {
  return (
    <ToastProvider>
      <GlobalProvider>
        <ShellInner {...props} />
      </GlobalProvider>
    </ToastProvider>
  );
}
